import { Col } from 'antd'
import { Formik } from 'formik'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import * as Yup from 'yup'
import { ContentContainer } from '../../components/custom_components/ContentContainer'
import PerFlexRow from '../../components/custom_components/FlexRow'
import MeetingBackground from '../../components/custom_components/Meeting/MeetingBackground'
import { PerModal } from '../../components/custom_components/Modal'
import PerSpacer from '../../components/custom_components/Spacer'
import PerText from '../../components/custom_components/Text'
import { FormItem } from '../../components/formik-component'
import { useCollapse } from '../../routes/collapse-context'
import { FullMeetingFragment } from '../../types'
import { isDefined } from '../../types.helper'
import { MeetingConfirmBtn } from '../appraisal/appraisal-flow/shared/MeetingStepButton'
import { AgendaOtherMeeting } from './AgendaOtherMeeting'
import { HintsOtherMeeting } from './HintsOtherMeeting'
import { OtherMeetingHeader } from './OtherMeetingHeader'
import { processOtherMeeting } from './OtherMeetings-helper'
import RichTextBox from '../../components/custom_components/RichTextBox'

type EmployeePrepareOtherMeetingProps = {
  meeting: FullMeetingFragment
}

const EmployeePrepareOtherMeeting: FC<EmployeePrepareOtherMeetingProps> = ({ meeting }) => {
  const [t] = useTranslation()
  const history = useHistory()
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const collapsed = useCollapse()

  const onModalCancel = () => {
    setModalOpen(false)
  }

  const redirect = () => {
    history.goBack()
  }

  return (
    <Formik
      initialValues={{ textAreaValue: '' }}
      enableReinitialize={true}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        setSubmitting(true)
        setModalOpen(true)
        await processOtherMeeting(t, meeting, values.textAreaValue, false)
        resetForm()
        setSubmitting(false)
        redirect()
      }}
      validateOnChange={true}
      validationSchema={Yup.object().shape({
        textAreaValue: Yup.string()
          .min(2, t('formError.commentLength'))
          .required(t('formError.required', { fieldName: t('otherMeetings.feedback.headline') })),
      })}
    >
      {(props) => {
        const { isSubmitting, handleSubmit, values, isValid, initialValues } = props
        const isViolatingForm = !isValid || initialValues.textAreaValue === values.textAreaValue

        return (
          <>
            <PerModal
              title={{ heading: t('otherMeetings.confirmModal.heading') }}
              size="small"
              centered
              visible={modalOpen}
              onCancel={onModalCancel}
              footer={{
                customBtns: [
                  {
                    text: t('global.button.edit'),
                    onClick: onModalCancel,
                    color: 'secondary',
                    icon: 'edit_fill',
                  },
                ],
                confirmBtn: {
                  disabled: isSubmitting,
                  isProcessing: { active: isSubmitting },
                  onClick: () => {
                    handleSubmit()
                  },
                },
              }}
            >
              <PerText mb="s">
                {t('otherMeetings.confirmModal.text', {
                  reviewerName: meeting.reviewer && meeting.reviewer.firstName,
                })}
              </PerText>
            </PerModal>

            <ContentContainer collapsed={collapsed}>
              <OtherMeetingHeader meeting={meeting} isPreparation />
              <MeetingBackground withHeader>
                <PerText appearance="tile-heading">{t(`meetingTypes.${meeting.type}`)}</PerText>

                <PerSpacer mt="m">
                  <HintsOtherMeeting meeting={meeting} className="otherMeetings-collapse" />
                </PerSpacer>

                <PerSpacer mt="xxxl">
                  <PerFlexRow>
                    <Col span={12}>
                      <PerText appearance="tile-heading" mb="s">
                        {t('otherMeetings.feedback.headline')}
                      </PerText>
                    </Col>
                    {!!meeting.other && isDefined(meeting.other.agenda) && (
                      <Col span={12}>
                        <PerText appearance="tile-heading" mb="s">
                          {t('otherMeetings.agendaHeadline')}
                        </PerText>
                      </Col>
                    )}
                  </PerFlexRow>
                  <PerFlexRow>
                    <Col span={12}>
                      <FormItem name="textAreaValue">
                        <RichTextBox
                          placeholder={t('otherMeetings.feedback.placeholder')}
                          data-e2e-other-meeting-notes
                          text={values.textAreaValue}
                          onChange={(text) => props.setFieldValue('textAreaValue', text)}
                        />
                      </FormItem>
                    </Col>
                    {!!meeting.other && isDefined(meeting.other.agenda) && (
                      <Col span={12}>
                        <AgendaOtherMeeting meeting={meeting} />
                      </Col>
                    )}
                  </PerFlexRow>
                </PerSpacer>
              </MeetingBackground>

              <PerFlexRow justify="end">
                <MeetingConfirmBtn
                  disabled={isViolatingForm || isSubmitting}
                  onClick={() => setModalOpen(true)}
                  text={t('global.button.confirm')}
                />
              </PerFlexRow>
            </ContentContainer>
          </>
        )
      }}
    </Formik>
  )
}

export default EmployeePrepareOtherMeeting
